import React, { useCallback, useEffect, useRef, useState } from 'react';
import GradingQuestionnaire from './GradingQuestionnaire';
import { MpStepper } from './MarketProfileShared';
import type { GradingAnswerSnapshot, GradingResultSnapshot } from './marketProfileStore';
import PositionQuestionnaire from './PositionQuestionnaire';
import {
  normalizePositionFormData,
  validateBaseSalaryStep,
  validateLtipStep,
  validateStiStep,
  type PositionFormData,
} from './positionFormModel';

const SHARE_STEPS = [
  'Базовый оклад',
  'STI',
  'LTIP',
  'Направление',
  'Роль',
  'Функционал',
];

type Props = {
  positionInitial: PositionFormData;
  onBack: () => void;
  onComplete: (
    position: PositionFormData,
    result: GradingResultSnapshot,
    answers: GradingAnswerSnapshot[],
  ) => void;
};

export const SHARE_QUESTIONNAIRE_LABEL = 'Поделиться компенсационным пакетом и данными о функционале';

export default function CombinedShareQuestionnaire({
  positionInitial,
  onBack,
  onComplete,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const phaseSeen = useRef(false);
  const positionRef = useRef<PositionFormData>(normalizePositionFormData(positionInitial));
  const [phase, setPhase] = useState<'position' | 'grading'>('position');
  const [positionStep, setPositionStep] = useState(0);
  const [gradingStep, setGradingStep] = useState(0);
  const [gradingMounted, setGradingMounted] = useState(false);
  const [sequenceError, setSequenceError] = useState<string | null>(null);

  const onFormChange = useCallback((data: PositionFormData) => {
    positionRef.current = data;
  }, []);

  const onPositionStep = useCallback((step: number) => {
    setPositionStep(step);
  }, []);

  const onGradingStep = useCallback((step: number) => {
    setGradingStep(step);
  }, []);

  useEffect(() => {
    if (!phaseSeen.current) {
      phaseSeen.current = true;
      return;
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rootRef.current?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }, [phase]);

  const advanceToGrading = (data: PositionFormData) => {
    positionRef.current = normalizePositionFormData(data);
    setSequenceError(null);
    setGradingMounted(true);
    setPhase('grading');
  };

  const finish = (result: GradingResultSnapshot, answers: GradingAnswerSnapshot[]) => {
    const data = normalizePositionFormData(positionRef.current);
    const err = validateBaseSalaryStep(data) || validateStiStep(data) || validateLtipStep(data);
    if (err) {
      setSequenceError(err);
      setPhase('position');
      return;
    }
    onComplete(data, result, answers);
  };

  const current = phase === 'position' ? positionStep : 3 + gradingStep;

  return (
    <div className="dcf-mp-form dcf-mp-share" ref={rootRef}>
      <button type="button" className="dcf-btn dcf-btn--ghost dcf-mp-back" onClick={onBack}>
        ← К профилю рынка
      </button>

      <header className="dcf-header">
        <h1 className="dcf-header__title">Компенсационный пакет и данные о функционале</h1>
        <p className="dcf-header__desc">
          Сначала вознаграждение, затем содержание роли. Это одна анкета:
          ответы не показываются в профиле и идут только в обезличенную статистику.
        </p>
      </header>

      <MpStepper steps={SHARE_STEPS} current={current} />
      <p className="dcf-mp-share__part">
        {phase === 'position' ? 'Компенсационный пакет' : 'Данные о функционале'}
      </p>
      {sequenceError && phase === 'position' && (
        <p className="dcf-mp-error" role="alert">{sequenceError}</p>
      )}

      <div hidden={phase !== 'position'}>
        <PositionQuestionnaire
          embedded
          initial={positionInitial}
          submitLabel="Далее"
          onComplete={advanceToGrading}
          onBack={onBack}
          onStepChange={onPositionStep}
          onFormChange={onFormChange}
        />
      </div>

      {gradingMounted && (
        <div hidden={phase !== 'grading'}>
          <GradingQuestionnaire
            embedded
            finishLabel="Отправить анкету"
            onComplete={finish}
            onBack={onBack}
            onBackFromStart={() => setPhase('position')}
            onStepChange={onGradingStep}
          />
        </div>
      )}
    </div>
  );
}
